/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleControlZoneProps {
  id: string;
  controlZoneId: string | null;
  vehicleId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class VehicleControlZoneRecord extends Record<VehicleControlZoneProps>({
  id: null as any,
  controlZoneId: null as any,
  vehicleId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    controlZoneId: string | null;
    vehicleId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): VehicleControlZoneRecord {
    if (json == null) {
      throw new Error('VehicleControlZoneRecord.fromJS: parameter cannot be null');
    }
    return new VehicleControlZoneRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleControlZoneJson.id',
      ),
      controlZoneId: json.controlZoneId,
      vehicleId: json.vehicleId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'VehicleControlZoneJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    controlZoneId: string | null;
    vehicleId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [VehicleControlZoneRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleControlZoneRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleControlZoneRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleControlZoneJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      controlZoneId: tryOrDefault(
        () => json.controlZoneId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'VehicleControlZoneJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: VehicleControlZoneProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleControlZoneRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleControlZoneViewModelBase implements VehicleControlZoneProps {
  protected readonly _props: VehicleControlZoneProps;
  private _json?: VehicleControlZoneProps;

  get id(): string {
    return this._props.id;
  }
  get controlZoneId(): string | null {
    return this._props.controlZoneId;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: VehicleControlZoneProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        controlZoneId: this._props.controlZoneId,
        vehicleId: this._props.vehicleId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleControlZoneEntities = (state: AppState) =>
  state.entityStores.vehicleControlZone.entities;

export const selectVehicleControlZoneById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleControlZone.entities.get(id);

export const selectVehicleControlZoneMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleControlZone.meta.get(id);

export const selectVehicleControlZoneAndMetadataById = (id: string) =>
  createSelector(
    selectVehicleControlZoneById(id),
    selectVehicleControlZoneMetadataById(id),
    (vehicleControlZone, meta) => ({ vehicleControlZone, meta }),
  );

export function selectVehicleControlZoneEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleControlZoneEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleControlZoneRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleControlZoneEntitiesByControlZoneId(controlZoneId: string) {
  return createSelector(selectAllVehicleControlZoneEntities, (entities) =>
    entities.filter((e) => e.controlZoneId === controlZoneId).valueSeq(),
  );
}

export function selectVehicleControlZoneEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllVehicleControlZoneEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectVehicleControlZoneEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllVehicleControlZoneEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
